/* Deux instances Flickity indépendantes ; cycle de vie lié au composant Shopify. */
if (!customElements.get('stacked-scroll-banners')) {
  customElements.define('stacked-scroll-banners', class extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.carousels = [];
      this.zones = Array.from(this.querySelectorAll('[data-stacked-slide]'));
      this.isScrolling = false;
      this.scrollTimer = null;
      this.isInViewport = true;
      this.trackTop = null;
      this.zoneHeights = null;

      const listen = (target, event, handler, options = {}) =>
        target.addEventListener(event, handler, { signal: this.lifecycle.signal, ...options });

      const initialize = () => {
        if (!this.isConnected || this.carousels.length || !window.Flickity) return;
        this.updateMetrics();
        this.zones.forEach(zone => this.createCarousel(zone, listen));
        this.updateScroll();
      };
      initialize();

      // Secours si le composant est inséré avant la fin des scripts différés.
      listen(window, 'load', () => {
        this.updateMetrics();
        initialize();
      });

      let lastScrollY = window.scrollY;
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        // Only trigger scroll pause if the page actually moved vertically
        if (Math.abs(currentScrollY - lastScrollY) > 2) {
          lastScrollY = currentScrollY;
          beginPageScroll();
        }
        updateScroll();
      };

      const handleScrollEnd = () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = null;
        this.isScrolling = false;
        if (this.isInViewport) {
          this.scheduleActive();
        }
      };

      const beginPageScroll = () => {
        if (!this.isScrolling) {
          this.isScrolling = true;
          this.clearAllTimers();
          // Finish an in-flight slide animation as soon as vertical scrolling starts.
          this.carousels.forEach(state => {
            state.instance.select(state.instance.selectedIndex, false, true);
          });
        }
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(handleScrollEnd, 200);
      };

      const updateScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.updateScroll();
        });
      };

      listen(window, 'scroll', handleScroll, { passive: true });
      listen(window, 'scrollend', () => handleScrollEnd(), { passive: true });

      listen(this, 'wheel', event => {
        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) beginPageScroll();
      }, { passive: true });

      let pageTouchStart = null;
      listen(this, 'touchstart', event => {
        if (event.touches.length === 1) {
          pageTouchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        }
      }, { passive: true });
      listen(this, 'touchmove', event => {
        if (!pageTouchStart || event.touches.length !== 1) return;
        const dx = Math.abs(event.touches[0].clientX - pageTouchStart.x);
        const dy = Math.abs(event.touches[0].clientY - pageTouchStart.y);
        if (dy > 8 && dy > dx * 1.2) beginPageScroll();
      }, { passive: true });
      listen(this, 'touchend', () => { pageTouchStart = null; }, { passive: true });
      listen(this, 'touchcancel', () => { pageTouchStart = null; }, { passive: true });

      listen(window, 'resize', () => {
        this.updateMetrics();
        updateScroll();
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          const [entry] = entries;
          this.isInViewport = entry ? entry.isIntersecting : true;
          if (this.isInViewport && !this.isScrolling) {
            this.scheduleActive();
          } else if (!this.isInViewport) {
            this.clearAllTimers();
          }
        }, { threshold: [0, 0.1] });
        this.observer.observe(this);
      }

      listen(this.motion, 'change', () => {
        this.carousels.forEach(state => {
          state.instance.select(state.instance.selectedIndex, false, true);
          state.pause?.toggleAttribute('hidden', this.motion.matches);
        });
        if (this.isInViewport && !this.isScrolling) {
          this.scheduleActive();
        }
        updateScroll();
      });

      listen(document, 'visibilitychange', () => {
        if (document.hidden) {
          this.clearAllTimers();
        } else if (this.isInViewport && !this.isScrolling) {
          this.scheduleActive();
        }
      });

      listen(this, 'shopify:block:select', event => {
        const state = this.carousels.find(item => item.slides.some(slide => slide.dataset.blockId === event.detail.blockId));
        if (!state) return;
        state.editing = true;
        state.instance.select(state.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId), false, true);
        this.schedule(state);
      });

      listen(this, 'shopify:block:deselect', () => this.carousels.forEach(state => {
        state.editing = false;
        if (this.isInViewport && !this.isScrolling) {
          this.scheduleActive();
        }
      }));

      this.updateScroll();
    }

    createCarousel(zone, listen) {
      const element = zone.querySelector('[data-banner-carousel]');
      const slides = Array.from(element.children);
      if (slides.length < 2) return;
      const state = { zone, element, slides, paused: false, focused: false, hovered: false, editing: false, manual: false };

      // Tuned spring and damping parameters for smooth, crisp transitions (0.45s) without sluggish lag or wobble
      const attraction = { slow: 0.04, normal: 0.08, fast: 0.12 };
      const friction = { slow: 0.35, normal: 0.45, fast: 0.5 };

      state.instance = new Flickity(element, {
        cellSelector: '[data-banner-slide]',
        cellAlign: 'left',
        percentPosition: true,
        wrapAround: true,
        adaptiveHeight: false,
        setGallerySize: false,
        prevNextButtons: false,
        pageDots: false,
        accessibility: false,
        autoPlay: false,
        draggable: false, // Prevents all touch hijacking so native vertical scroll is always 100% free
        selectedAttraction: attraction[this.dataset.speed] || 0.08,
        friction: friction[this.dataset.speed] || 0.45,
        on: { ready: () => element.classList.add('is-ready') }
      });

      this.carousels.push(state);
      element.tabIndex = 0;
      element.setAttribute('aria-label', `${zone.getAttribute('aria-label')} : utiliser les flèches gauche et droite`);
      const controls = zone.querySelector('[data-controls]');
      if (controls) controls.hidden = false;
      state.pause = controls?.querySelector('[data-pause]');
      state.pause?.toggleAttribute('hidden', this.motion.matches);

      const sync = () => {
        const selected = state.instance.selectedIndex;
        slides.forEach((slide, index) => {
          const hidden = index !== selected;
          slide.inert = hidden;
          slide.setAttribute('aria-hidden', String(hidden));
        });
        if (controls) {
          controls.querySelectorAll('[data-page]').forEach(button => {
            if (Number(button.dataset.page) === selected) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
          });
        }
        if (state.manual) {
          const status = zone.querySelector('[data-status]');
          if (status) status.textContent = `Bannière ${selected + 1} sur ${slides.length}`;
        }
      };

      state.instance.on('select', sync);

      // Re-arm autoplay only when the current slide has settled into view
      state.instance.on('settle', () => {
        if (!this.isScrolling && this.isInViewport) {
          this.schedule(state);
        }
      });

      // Passive touch swipe detection for horizontal navigation without blocking vertical scroll
      let touchStartX = 0;
      let touchStartY = 0;
      let touchStartTime = 0;

      listen(element, 'touchstart', (e) => {
        if (e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
      }, { passive: true });

      listen(element, 'touchend', (e) => {
        if (!touchStartTime || e.changedTouches.length !== 1) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        const dy = e.changedTouches[0].clientY - touchStartY;
        const dt = Date.now() - touchStartTime;
        touchStartTime = 0;
        if (dt < 600 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          state.manual = true;
          if (dx < 0) {
            state.instance.next(true);
          } else {
            state.instance.previous(true);
          }
          state.manual = false;
        }
      }, { passive: true });

      const select = index => {
        state.manual = true;
        state.instance.select(index, true, this.motion.matches);
        state.manual = false;
      };

      if (controls) {
        listen(controls, 'click', event => {
          const button = event.target.closest('button');
          if (!button) return;
          if (button.hasAttribute('data-previous')) select(state.instance.selectedIndex - 1);
          if (button.hasAttribute('data-next')) select(state.instance.selectedIndex + 1);
          if (button.hasAttribute('data-page')) select(Number(button.dataset.page));
          if (button === state.pause) {
            state.paused = !state.paused;
            button.setAttribute('aria-pressed', String(state.paused));
            button.setAttribute('aria-label', `${state.paused ? 'Reprendre' : 'Mettre en pause'} — ${zone.getAttribute('aria-label')}`);
            button.textContent = state.paused ? '▶' : 'Ⅱ';
            if (state.paused) {
              clearTimeout(state.timer);
              state.timer = null;
            } else {
              this.schedule(state);
            }
          }
        });
      }

      listen(element, 'keydown', event => {
        if (event.target !== element || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        select(state.instance.selectedIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });

      // Pause on hover only when enabled in section settings
      if (this.dataset.pauseHover === 'true') {
        listen(zone, 'mouseenter', () => {
          state.hovered = true;
          clearTimeout(state.timer);
          state.timer = null;
        });
        listen(zone, 'mouseleave', () => {
          state.hovered = false;
          if (!this.isScrolling && this.isInViewport) this.schedule(state);
        });
      }

      listen(zone, 'focusin', () => {
        state.focused = true;
        clearTimeout(state.timer);
        state.timer = null;
      });
      listen(zone, 'focusout', event => {
        state.focused = zone.contains(event.relatedTarget);
        if (!state.focused && !this.isScrolling && this.isInViewport) this.schedule(state);
      });

      sync();
      if (!this.isScrolling && this.isInViewport) {
        this.schedule(state);
      }
    }

    getActiveZoneIndex() {
      const rect = this.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.bottom <= 0 || rect.top >= windowHeight) return -1;
      const scrollOffset = -rect.top;
      const height0 = (this.zoneHeights && this.zoneHeights[0]) || (this.zones[0] ? this.zones[0].offsetHeight : windowHeight);
      if (this.zones.length > 1 && scrollOffset >= height0 * 0.5) {
        return 1;
      }
      return 0;
    }

    clearAllTimers() {
      this.carousels.forEach(state => {
        clearTimeout(state.timer);
        state.timer = null;
      });
    }

    scheduleActive() {
      const activeIndex = this.getActiveZoneIndex();
      this.carousels.forEach((state, index) => {
        if (index === activeIndex) {
          this.schedule(state);
        } else {
          clearTimeout(state.timer);
          state.timer = null;
        }
      });
    }

    schedule(state) {
      clearTimeout(state.timer);
      state.timer = null;
      if (this.dataset.autoplay !== 'true' || this.motion.matches || document.hidden || state.paused ||
          state.focused || state.editing || this.isScrolling || !this.isInViewport ||
          (state.hovered && this.dataset.pauseHover === 'true')) return;

      const activeIndex = this.getActiveZoneIndex();
      const stateIndex = this.carousels.indexOf(state);
      if (stateIndex !== activeIndex) return;

      const delay = Number(this.dataset.delay) || 4000;
      state.timer = setTimeout(() => {
        if (this.isScrolling || !this.isInViewport) return;
        if (this.getActiveZoneIndex() !== stateIndex) return;
        state.instance.next(true);
      }, delay);
    }

    updateMetrics() {
      const scrollY = window.scrollY;
      this.trackTop = this.getBoundingClientRect().top + scrollY;
      this.zoneHeights = this.zones.map(zone => zone.offsetHeight);
    }

    updateScroll() {
      const rect = this.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.bottom <= 0 || rect.top >= windowHeight) {
        this.clearAllTimers();
        return;
      }

      const activeIndex = this.getActiveZoneIndex();
      this.carousels.forEach((state, index) => {
        if (index !== activeIndex && state.timer) {
          clearTimeout(state.timer);
          state.timer = null;
        }
      });

      const scrollY = window.scrollY;
      if (this.trackTop == null || !this.zoneHeights || !this.zoneHeights.length) {
        this.updateMetrics();
      }
      const trackTop = this.trackTop;
      this.zones.forEach((zone, index) => {
        const height = (this.zoneHeights && this.zoneHeights[index]) || zone.offsetHeight;
        const progress = this.motion.matches || index === this.zones.length - 1 || !height ? 0 :
          Math.min(1, Math.max(0, (scrollY - trackTop) / height));
        zone.style.setProperty('--stack-push', `${-height * progress}px`);
        zone.style.setProperty('--stack-opacity', Math.max(0, 1 - progress * 1.5));
        zone.style.setProperty('--stack-overlay', progress * 0.7);
        zone.style.setProperty('--stack-parallax', `${progress * -20}%`);
      });
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.observer?.disconnect();
      clearTimeout(this.scrollTimer);
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.carousels?.forEach(state => {
        clearTimeout(state.timer);
        state.timer = null;
        state.instance.destroy();
        state.element.classList.remove('is-ready');
        state.element.removeAttribute('tabindex');
        const controls = state.zone.querySelector('[data-controls]');
        if (controls) controls.hidden = true;
        state.slides.forEach((slide, index) => {
          slide.inert = index !== 0;
          slide.setAttribute('aria-hidden', String(index !== 0));
        });
      });
      this.carousels = [];
      this.lifecycle = null;
    }
  });
}
